/**
 * SelectTypeStep.js
 *
 * Step 1: Select the type of checklist to import.
 */

import React from 'react';
import { AlertTriangle, Anchor, Users, Compass, Settings, Calendar, Clock, Check, PlusCircle } from 'lucide-react';
import { CHECKLIST_TYPE_CONFIG } from '../../../../../../config/operations/checklistImportConfig';

// Icon mapping
const ICON_MAP = {
  AlertTriangle,
  Anchor,
  Users,
  Compass,
  Settings,
  Calendar,
  Clock,
  PlusCircle
};

// Color mapping
const COLOR_MAP = {
  red: 'bg-red-100 text-red-600 border-red-200',
  blue: 'bg-blue-100 text-blue-600 border-blue-200',
  green: 'bg-green-100 text-green-600 border-green-200',
  cyan: 'bg-cyan-100 text-cyan-600 border-cyan-200',
  amber: 'bg-amber-100 text-amber-600 border-amber-200',
  slate: 'bg-slate-100 text-slate-600 border-slate-200',
  purple: 'bg-purple-100 text-purple-600 border-purple-200',
  indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200'
};

const SelectTypeStep = ({ selectedType, onSelectType }) => {
  // Separate custom type to show at end
  const allTypes = Object.values(CHECKLIST_TYPE_CONFIG);
  const standardTypes = allTypes.filter(t => !t.isCustomType);
  const customTypes = allTypes.filter(t => t.isCustomType);
  const types = [...standardTypes, ...customTypes];

  return (
    <div className="p-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-slate-800">What type of checklist are you importing?</h3>
        <p className="text-sm text-slate-500 mt-1">
          Select the category that best matches your document
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {types.map((type) => {
          const Icon = ICON_MAP[type.icon] || Settings;
          const colorClasses = COLOR_MAP[type.color] || COLOR_MAP.slate;
          const isSelected = selectedType === type.id;

          return (
            <button
              key={type.id}
              onClick={() => onSelectType(type.id)}
              className={`
                relative flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all
                ${isSelected
                  ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-200'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }
              `}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClasses}`}>
                <Icon className="h-5 w-5" />
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800">{type.label}</p>
                <p className="text-sm text-slate-500 mt-0.5">{type.description}</p>
              </div>

              {isSelected && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                  <Check className="h-4 w-4 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-slate-50 rounded-lg">
        <p className="text-sm text-slate-600">
          <strong>Tip:</strong> The AI will extract checklist items based on this type.
          Choose the closest match - you can adjust individual items later.
        </p>
      </div>
    </div>
  );
};

export default SelectTypeStep;
